import * as React from 'react';
import { css, IconButton, IContextualMenuItem, Pivot, PivotItem } from 'office-ui-fabric-react';
import HeaderSecondaryText, { Props as HeaderSecondaryTextProps } from './HeaderSecondaryText';
import { Dictionary, IBaseEntity } from '../../../entities/common';
import { useResizeObject } from '../../utils/effects';

export interface IExtraTab {
    key: string;
    label: string;
    className?: string;
    isVisible?: (entity: IBaseEntity) => boolean;
    renderHeaderSecondaryText: (entity: IBaseEntity) => string | JSX.Element;
    renderBody: (entity: IBaseEntity, onChange: (fieldName?: string, fieldValue?: unknown, extra?: Dictionary<unknown>) => void, markDirty: () => void) => JSX.Element;
}

type ExtraTabsProps = {
    selectedTab?: IExtraTab;
    onTabChange: (key: string) => void;
    isTabsDisabled: boolean;
    extraTabs: IExtraTab[];
    renderHeaderSecondaryText?: (entity: IBaseEntity) => string | JSX.Element;
}

const DetailsTabKey = "details";

type TabHeader = { key: string, label: string, className?: string };

const OverflowButtonWidth = 26;

const useVisibleTabCount = (tabs: TabHeader[]) => {
    const [measured, setMeasured] = React.useState<{ measuredFor: string, widths: number[] }>();
    const [available, setAvailable] = React.useState<number>();
    const onResize = React.useCallback((offsetWidth: number) => setAvailable(offsetWidth), []);
    const holderRef = useResizeObject<HTMLDivElement>(onResize);
    const stripRef = React.useRef<HTMLDivElement>(null);

    const measuredFor = `${tabs.map(_ => _.label).join("|")}@${available}`;
    const widths = measured?.measuredFor === measuredFor ? measured.widths : undefined;
    React.useLayoutEffect(() => {
        if (widths || !stripRef.current) {
            return;
        }
        const links = stripRef.current.querySelectorAll<HTMLElement>(".ms-Pivot-link");
        if (links.length === tabs.length) {
            setMeasured({ measuredFor, widths: Array.from(links).map(_ => _.getBoundingClientRect().width) });
        }
    });

    const visibleCount = React.useMemo(
        () => {
            if (!widths || available === undefined) {
                return tabs.length;
            }
            let used = 0;
            let count = 0;
            while (count < widths.length && used + widths[count] <= available) {
                used += widths[count];
                count++;
            }
            if (count === widths.length) {
                return count;
            }
            while (count > 0 && used + OverflowButtonWidth > available) {
                count--;
                used -= widths[count];
            }
            return count;
        },
        [widths, available, tabs.length]);

    return { visibleCount, holderRef, stripRef };
}

const ExtraTabs = React.memo((props: ExtraTabsProps & HeaderSecondaryTextProps) => {
    const { isTabsDisabled, extraTabs, onTabChange, selectedTab, entity } = props;
    // While the panel is invalid the user must stay on the tab they are editing: every tab EXCEPT the active one is
    // disabled - including Details. Otherwise an invalid value entered on an extra tab (e.g. a negative assignment
    // Units) lets the user escape to the always-enabled Details tab and then strands them there, because the tab
    // holding the invalid value is disabled and cannot be reopened to fix it.
    const selectedKey = selectedTab?.key ?? DetailsTabKey;
    const isDisabled = React.useCallback((key: string) => isTabsDisabled && key !== selectedKey, [isTabsDisabled, selectedKey]);

    const visibleExtraTabs = React.useMemo(
        () => extraTabs.filter(_ => !_.isVisible || _.isVisible(entity)),
        [extraTabs, entity]);
    const tabs = React.useMemo<TabHeader[]>(
        () => [{ key: DetailsTabKey, label: "Details" } as TabHeader]
            .concat(visibleExtraTabs.map(_ => ({ key: _.key, label: _.label, className: _.className }))),
        [visibleExtraTabs]);

    const { visibleCount, holderRef, stripRef } = useVisibleTabCount(tabs);
    const overflowTabs = tabs.slice(visibleCount);
    const isOverflowSelected = overflowTabs.some(_ => _.key === selectedKey);

    const onLinkClick = React.useCallback(
        (item?: PivotItem) => item?.props.itemKey && onTabChange(item.props.itemKey),
        [onTabChange]);

    const overflowItems = React.useMemo<IContextualMenuItem[]>(
        () => tabs.slice(visibleCount).map(_ => ({
            key: _.key,
            text: _.label,
            className: css("panel-tab-menu-item", _.className),
            canCheck: true,
            checked: _.key === selectedKey,
            disabled: isDisabled(_.key),
            onClick: () => onTabChange(_.key)
        })),
        [tabs, visibleCount, selectedKey, isDisabled, onTabChange]);

    return <>
        {!!visibleExtraTabs.length &&
            <div className="panel-tabs-holder" ref={holderRef}>
                <div className="panel-tabs-strip" ref={stripRef}>
                    <Pivot
                        headersOnly={true}
                        className="panel-tabs"
                        selectedKey={isOverflowSelected ? null : selectedKey}
                        onLinkClick={onLinkClick}>
                        {tabs.slice(0, visibleCount).map(_ => <PivotItem
                            key={_.key}
                            itemKey={_.key}
                            headerText={_.label}
                            headerButtonProps={{
                                ...(isDisabled(_.key) ? { 'disabled': true, 'style-color': 'grey' } : undefined),
                                //classname does not work with fabric pivot, need to use [data-class-name='value'] approach
                                ...(_.className ? { 'data-class-name': _.className } : undefined)
                            }} />)}
                    </Pivot>
                </div>
                {!!overflowTabs.length && <IconButton
                    className={css("panel-tabs-overflow", isOverflowSelected && "is-selected")}
                    menuIconProps={{ iconName: 'More' }}
                    title="More tabs"
                    ariaLabel="More tabs"
                    menuProps={{ items: overflowItems, className: "panel-tabs-overflow-menu" }} />}
            </div>
        }
        {
            selectedTab
                ?  selectedTab.renderHeaderSecondaryText(entity)
                : props.renderHeaderSecondaryText 
                    ? props.renderHeaderSecondaryText(entity)
                    : <HeaderSecondaryText {...props} />
        }
    </>;
})

export default ExtraTabs;
